import React, { useState, useEffect } from "react";
import axios from "axios";
import { Download, FileText, Check, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API, getContentSource } from "@/lib/constants";
import { getHutk } from "@/lib/hubspot";

const STORAGE_KEY = "dmn_swipefile_100hooks";

export const SwipeFileGate = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setDownloadUrl(saved);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please enter your first name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/lead-magnets/download`, {
        first_name: firstName,
        email,
        magnet: "100-hooks-swipe-file",
        source_page: window.location.pathname,
        hutk: getHutk(),
      });
      localStorage.setItem(STORAGE_KEY, res.data.download_url);
      navigate("/thank-you/swipe-file");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Please check your email and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      id="swipe-file"
      className="mt-14 scroll-mt-28 relative overflow-hidden rounded-2xl border border-[#00E676]/25 bg-gradient-to-br from-[#00E676]/[0.08] via-black/40 to-[#6366F1]/[0.06] p-8 sm:p-10"
      data-testid="swipe-file-gate"
    >
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#00E676]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00E676]/15 border border-[#00E676]/30 text-[#00E676] text-xs font-bold uppercase tracking-widest mb-5">
          <FileText className="w-3.5 h-3.5" /> Free Swipe File
        </span>

        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
          Get All 100 Hooks That Stop the Scroll
        </h2>
        <p className="text-gray-400 text-[15px] leading-relaxed max-w-xl mb-7">
          Ten goals, 100 fill-in-the-blank templates, and the psychology behind each one — so you
          never stare at a blank caption again.
        </p>

        {downloadUrl ? (
          <div data-testid="swipe-file-success">
            <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
              <Check className="w-4 h-4" /> Unlocked — your swipe file is ready.
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer" data-testid="swipe-file-download-btn">
                <Button className="btn-ninja px-6">
                  <Download className="w-4 h-4 mr-2" /> Download the 100-Hook Swipe File
                </Button>
              </a>
              <Link to="/contact" className="text-sm text-gray-400 hover:text-[#00E676] transition-colors inline-flex items-center gap-1" data-testid="swipe-file-work-with-us-link">
                or work with Digital Media Ninja <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} id="swipe-file-form" name="swipe-file-form" className="max-w-xl" data-testid="swipe-file-form">
            <input type="hidden" name="content_source" value={getContentSource()} />
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Input
                type="text"
                name="first_name"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="bg-black/40 border-white/10 text-white placeholder:text-gray-600 h-11"
                data-testid="swipe-file-name-input"
              />
              <Input
                type="email"
                name="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-black/40 border-white/10 text-white placeholder:text-gray-600 h-11"
                data-testid="swipe-file-email-input"
              />
            </div>
            <Button type="submit" disabled={submitting} className="btn-ninja w-full sm:w-auto px-8 h-11" data-testid="swipe-file-submit-btn">
              {submitting ? "Unlocking..." : (<><Download className="w-4 h-4 mr-2" /> Download the 100-Hook Swipe File</>)}
            </Button>
            <p className="text-gray-600 text-xs mt-3">Instant download. No spam — ever.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default SwipeFileGate;
